import InsertDriveFile from "@mui/icons-material/InsertDriveFile";
import Remove from "@mui/icons-material/Remove";
import {
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { Dropzone } from "components/Dropzone";
import { DropzoneErrorDisplay } from "components/DropzoneErrorDisplay";
import { FacilityPicker } from "components/FacilityPicker";
import { FileRejection } from "react-dropzone";

import { useBillOfLadingUpload } from "./useBillOfLadingUpload";

type BillOfLadingUploadDialogProps = {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  /** Prefill when the entry point is facility-scoped; still editable — decision 4 always shows the picker. */
  facilityId?: string;
  /**
   * Fired when extraction has finished and the user clicks "Review" on the
   * analyzing dialog. The caller opens the review dialog with the document id
   * and the confirmed facility — this dialog only handles upload + handoff.
   */
  onExtractionReady?: (documentIds: string[], facilityId: string) => void;
};

function formatFileSize(sizeInBytes: number): string {
  if (sizeInBytes < 1024) return `${sizeInBytes} B`;
  if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(2)} KB`;
  return `${(sizeInBytes / 1024 / 1024).toFixed(2)} MB`;
}

function SelectedFileRow({
  file,
  onRemove,
  disabled,
}: {
  file: File;
  onRemove: () => void;
  disabled: boolean;
}) {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <ListItem dense sx={{ bgcolor: theme.palette.encampBlue.light, borderRadius: "4px" }}>
        <ListItemIcon sx={{ minWidth: "unset", paddingRight: theme.spacing(2) }}>
          <InsertDriveFile />
        </ListItemIcon>
        <ListItemText primary={file.name} secondary={formatFileSize(file.size)} />
      </ListItem>
      <Tooltip title="Remove file">
        <IconButton size="medium" aria-label="remove-file" onClick={onRemove} disabled={disabled}>
          <Remove />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function DropRejectionList({
  dropRejectedFiles,
  setDropRejectedFiles,
}: {
  dropRejectedFiles: FileRejection[];
  setDropRejectedFiles: (files: FileRejection[]) => void;
}) {
  if (dropRejectedFiles.length === 0) return null;
  return (
    <Stack spacing={1}>
      {dropRejectedFiles.map(({ file: rejectedFile, errors }, index) => (
        <DropzoneErrorDisplay
          key={`${rejectedFile.name}-${index}`}
          file={rejectedFile}
          index={index}
          errors={errors}
          setDropRejectedFiles={setDropRejectedFiles}
          dropRejectedFiles={dropRejectedFiles}
        />
      ))}
    </Stack>
  );
}

/**
 * Upload → extract handoff for a bill of lading. State and the submit/discard
 * handlers live in `useBillOfLadingUpload`; this component is layout only.
 */
export function BillOfLadingUploadDialog({
  open,
  onClose,
  tenantId,
  facilityId: initialFacilityId,
  onExtractionReady,
}: BillOfLadingUploadDialogProps) {
  const theme = useTheme();
  const upload = useBillOfLadingUpload({ tenantId, initialFacilityId, onClose, onExtractionReady });

  if (!open) return null;

  return (
    <>
      <Dialog
        open={upload.uploadDialogOpen}
        onClose={upload.handleClose}
        fullWidth
        maxWidth="sm"
        title="Add Bill of Lading"
        hideCancelButton
        primaryActionButtonProps={{
          hideStartIcon: true,
          saveText: upload.canSubmit ? "Add Bill of Lading" : "Close",
          onClick: upload.canSubmit ? upload.handleSubmit : upload.handleClose,
          disabled: upload.isUploading || (!!upload.file && !upload.facilityId),
          loading: upload.isUploading,
          loadingText: "Uploading...",
        }}
      >
        <Stack spacing={2}>
          <Typography color="text.secondary">
            Attach a carrier&apos;s bill of lading. This is not an EPA hazardous waste manifest —
            it&apos;s the shipper&apos;s own shipping record, and we&apos;ll read what we can from
            it automatically.
          </Typography>

          <FacilityPicker
            label="Facility"
            facilityQueryVariant="waste"
            textFieldProps={{ required: true }}
            value={upload.facility ?? initialFacilityId ?? null}
            onChange={upload.setFacility}
            disabled={upload.isUploading}
            invalid={false}
            isTouched={false}
            isDirty={false}
            isValidating={false}
          />

          <Dropzone
            label="Drag and drop your document or click to select a file"
            dragActiveText="Drop file here"
            onDrop={upload.handleDrop}
            accept=".pdf"
            multiple={false}
            disabled={upload.isUploading}
            sx={{ height: theme.spacing(14) }}
            displayUploadErrors={false}
            onDropRejected={upload.setDropRejectedFiles}
          />

          {upload.file && (
            <SelectedFileRow
              file={upload.file}
              onRemove={() => upload.setFile(null)}
              disabled={upload.isUploading}
            />
          )}

          <DropRejectionList
            dropRejectedFiles={upload.dropRejectedFiles}
            setDropRejectedFiles={upload.setDropRejectedFiles}
          />
        </Stack>
      </Dialog>

      {upload.discardConfirmOpen && (
        <ConfirmDialog
          open
          onClose={() => upload.setDiscardConfirmOpen(false)}
          onConfirm={upload.handleDiscard}
          header="Discard this upload?"
          info="This permanently deletes the uploaded document. This can't be undone."
          confirmText="Discard"
          loading={upload.isDiscarding}
        />
      )}
    </>
  );
}
